import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { useRollout } from '../rollout/RolloutContext';
import type { PlanStepView } from '../rollout/rolloutModel';
import { rolloutMessages } from '../rollout/rolloutMessages';
import { StepCard } from '../rollout/StepCard';
import { planDayDate, planStepTitle } from '../rolloutDayCopy';
import { reelsPlanMessages } from './reelsPlanMessages';
import { foldReelsSteps, REELS_PLAN_FOLD_NAMES, REELS_PLAN_VISIBLE_STEPS } from './reelsPlanModel';
/* eslint-disable css-modules/no-unused-class -- each part uses its own slice of the two stylesheets */
import rolloutStyles from '../rollout/Rollout.module.scss';
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanStepsProps {
  steps: PlanStepView[];
}

/**
 * The steps of an AI reels plan (design rollout-plan f3, f6): the step cards of the rollout, one
 * for each reel. The posts of one day share its date, and each names its post ("Post 2 of 3"). The
 * sheet shows 5 days and folds the others in one line ("2 more days: Fri 18 Jun, Sun 20 Jun"; "6
 * more posts: ..." with more posts a day); a click shows all. A skipped step is one line with
 * "Undo skip".
 */
const ReelsPlanStepsComponent: React.FC<ReelsPlanStepsProps> = ({ steps }) => {
  const intl = useIntl();
  const { actions, canAct, plan } = useRollout();
  const [showAll, setShowAll] = useState(false);
  const showEvery = useCallback(() => setShowAll(true), []);
  const live = useMemo(() => steps.filter((step) => step.day.skipped !== true), [steps]);
  const skipped = useMemo(() => steps.filter((step) => step.day.skipped === true), [steps]);
  // A plan with more posts a day shows the same number of days: its posts of one day stay together.
  const postsPerDay = plan.reelsPlan?.postsPerDay ?? 1;
  const { visible, hidden } = useMemo(
    () => foldReelsSteps(live, showAll, REELS_PLAN_VISIBLE_STEPS * postsPerDay),
    [live, postsPerDay, showAll]
  );
  const hiddenDays = useMemo(() => {
    // The posts of one day share the date: the line names each day once.
    const dates = Array.from(new Set(hidden.map((step) => step.date)));
    const names = dates.slice(0, REELS_PLAN_FOLD_NAMES).map((date) => planDayDate(intl, date));
    return dates.length > names.length ? `${names.join(', ')}, …` : names.join(', ');
  }, [hidden, intl]);

  return (
    <>
      <div className={clsx(rolloutStyles.steps, 'pt-2')}>
        {visible.map((step, index) => (
          <StepCard key={step.key} step={step} isFirst={index === 0} isLast={index === visible.length - 1} />
        ))}
        {skipped.map((step) => (
          <div key={`skipped-${step.key}`} className={rolloutStyles.skipped}>
            <FontAwesomeIcon icon={faXmark} />
            <span>
              {intl.formatMessage(rolloutMessages.skippedLine, { name: planStepTitle(intl, plan, step.day) })}
            </span>
            <button
              type="button"
              className={rolloutStyles.link}
              disabled={!canAct || actions.busy !== null}
              onClick={() => {
                actions.unskip('plan', step.key as RolloutDayKey).catch(console.error);
              }}
            >
              {intl.formatMessage(rolloutMessages.undoSkip)}
            </button>
          </div>
        ))}
      </div>
      {hidden.length > 0 && (
        <div className={styles.more}>
          <button type="button" className={rolloutStyles.link} onClick={showEvery}>
            {intl.formatMessage(postsPerDay > 1 ? reelsPlanMessages.morePosts : reelsPlanMessages.moreDays, {
              count: hidden.length,
              days: hiddenDays,
            })}
          </button>
        </div>
      )}
    </>
  );
};

export const ReelsPlanSteps = TypedMemo(ReelsPlanStepsComponent);
